import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import { moment } from '@pbt/pbt-ui-components'

import { RxPrescriptionOrigin } from '~/api/graphql/generated/types'
import { useNeedsLicenseEntry } from '~/components/dashboard/prescription/prescription-editor/hooks/useNeedsLicenseEntry'
import usePrescriptionEditor from '~/components/dashboard/prescription/prescription-editor/hooks/usePrescriptionEditor'
import PrescriptionBody from '~/components/dashboard/prescription/prescription-editor/PrescriptionBody'
import PrescriptionHeader from '~/components/dashboard/prescription/prescription-editor/PrescriptionHeader'
import { PrescriptionWorkflowType } from '~/constants/PrescriptionWorkflow'
import { useGetIsCompoundingReasonDetailsRequired } from '~/store/hooks/prescription'
import type { CRUDOrderOptions, Order, Prescription } from '~/types'

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
  },
}))

export interface EditPrescriptionStepHandle {
  getCurrentDoctorHasDvmLicense: () => boolean | null
  getHeader: () => React.ReactNode
  getPrescription: () => Prescription
  validate: () => boolean
}

interface EditPrescriptionStepProps {
  clientId: string
  onApproveButtonDisabledChange?: (isDisabled: boolean) => void
  onCancel?: () => void
  onProceed?: () => void
  patientId: string
  prescription: Prescription
}

type SignatureOverride = {
  signatureUrl?: string
  signedDate?: string
  signer: undefined
  signerId?: string
}

export const EditPrescriptionStep = forwardRef<
  EditPrescriptionStepHandle,
  EditPrescriptionStepProps
>(function EditPrescriptionStep(
  {
    clientId,
    patientId,
    prescription,
    onProceed,
    onCancel,
    onApproveButtonDisabledChange,
  },
  ref,
) {
  const classes = useStyles()
  const getIsCompoundingReasonDetailsRequired =
    useGetIsCompoundingReasonDetailsRequired()

  const [signatureOverride, setSignatureOverride] =
    useState<SignatureOverride | null>(null)

  // Track prescription ID to detect actual prescription changes vs re-renders
  const prescriptionIdRef = useRef(prescription.id)

  // Reset signature state only when prescription ID actually changes (not on every re-render)
  // This prevents race condition where signature update is lost due to parent re-render
  useEffect(() => {
    if (prescription.id !== prescriptionIdRef.current) {
      prescriptionIdRef.current = prescription.id
      setSignatureOverride(null) // Clear override when editing different prescription
    }
  }, [prescription.id])

  // Transform to Vet-initiated to display as Active Rx (shows Chewy Items, autoship, etc.)
  // DO NOT include signatureOverride here - it would cause usePrescriptionEditor to re-initialize and reset fields
  const editablePrescription = useMemo(
    () =>
      ({
        ...R.omit(['stateId'], prescription),
        id: prescription.id,
        origin: RxPrescriptionOrigin.Vet,
      }) as Prescription,
    [prescription],
  )

  const {
    validate,
    refs,
    fields,
    prescription: currentPrescription,
    activeWorkflow,
    handleSetActiveWorkflow,
    signatureData,
    isCompoundingReasonRequired,
    isCustomCompound,
    ...prescriptionBodyProps
  } = usePrescriptionEditor({
    clientId,
    patientId,
    prescription: editablePrescription,
    areChargesPostedAndEditable: true, // Lock workflow to Active Rx
    disableWorkflowSelector: true, // Disable workflow radio buttons - locked to Chewy Active Rx
    outsideSoap: true,
    showTaskCreationInEditMode: true,
    skipOrderFetching: true, // Skip fetching - search result doesn't have complete data
    onClose: () => {
      onCancel?.()
    },
    onSave: (_updated: Order, options: CRUDOrderOptions) => {
      if (!options.isActiveRxSaveDraft) {
        onProceed?.()
      }
    },
  })

  // Force Chewy Active Rx workflow for swap product flow (shows as "chewy.com" in dropdown)
  useEffect(() => {
    if (
      activeWorkflow &&
      activeWorkflow !== PrescriptionWorkflowType.CHEWY_ACTIVE_RX
    ) {
      handleSetActiveWorkflow(PrescriptionWorkflowType.CHEWY_ACTIVE_RX)
    }
  }, [activeWorkflow, handleSetActiveWorkflow])

  // Check if approve button should be disabled due to missing DVM license or state mismatch
  const needsLicenseEntry = useNeedsLicenseEntry(
    prescriptionBodyProps.isDvmLicenseRequired,
    prescriptionBodyProps.currentDoctorHasDvmLicense,
    prescriptionBodyProps.currentDoctorHasMatchingStateLicense,
  )

  useEffect(() => {
    onApproveButtonDisabledChange?.(needsLicenseEntry)
  }, [needsLicenseEntry, onApproveButtonDisabledChange])

  const getPrescriptionData = useCallback((): Prescription => {
    const administrationData = refs.administrationSectionRef.current?.get()
    const quantityData = refs.quantitySectionRef.current?.get()
    const refillableData = refs.refillableSectionRef.current?.get()
    const taskData = refs.taskSectionRef.current?.get()
    const dispensedFromData = refs.dispensedFromSectionRef.current?.get()
    const chargeData = refs.chargeSectionRef.current?.get()
    const controlledSubstanceData =
      refs.controlledSubstanceSectionRef.current?.get()
    const discussedWithClientData = refs.discussedWithClientRef.current?.get()
    const drugCompoundInfoData = refs.drugCompoundInfoRef.current?.get()

    const result = {
      ...currentPrescription,
      ...administrationData,
      ...quantityData,
      ...refillableData,
      ...dispensedFromData,
      ...(chargeData || {}),
      ...(controlledSubstanceData || {}),
      ...(discussedWithClientData || {}),
      ...signatureData,
      ...(signatureOverride != null
        ? {
            signerId: signatureOverride.signerId,
            signatureUrl: signatureOverride.signatureUrl,
            signer: signatureOverride.signer, // undefined to clear stale signer object
            signedDate: signatureOverride.signedDate,
          }
        : {}),
      ...(isCustomCompound
        ? {
            drugInfo: {
              ...(currentPrescription.drugInfo?.customCompound
                ? currentPrescription.drugInfo
                : {}),
              ...(drugCompoundInfoData || {}),
              customCompound: true,
            },
          }
        : {}),
      doctorId: fields.doctorId.value,
      partNumber: fields.chewyItem.value,
      compoundingReasonId: isCompoundingReasonRequired
        ? fields.compoundingReasonId.value
        : null,
      compoundingReason:
        isCompoundingReasonRequired &&
        getIsCompoundingReasonDetailsRequired(fields.compoundingReasonId.value)
          ? fields.compoundingReason.value
          : null,
      retailOrderLineItem: {
        ...currentPrescription.retailOrderLineItem,
        autoshipFrequency: fields.enableAutoship?.value
          ? fields.autoshipFrequency?.value
          : null,
        autoshipUnitId: fields.enableAutoship?.value
          ? fields.autoshipUnitId?.value
          : null,
      },
      ...(fields.automaticallyCreateTask.value
        ? {
            task: {
              ...(taskData || currentPrescription.task || {}),
              shouldCreateTask: true,
            },
          }
        : {
            task: null,
            clientTasks: null,
          }),
    } as Prescription

    return result
  }, [
    currentPrescription,
    refs,
    fields,
    signatureData,
    signatureOverride,
    isCompoundingReasonRequired,
    isCustomCompound,
    getIsCompoundingReasonDetailsRequired,
  ])

  const renderHeader = useCallback(
    () => (
      <Box>
        <PrescriptionHeader
          clientId={clientId}
          patientId={patientId}
          prescription={editablePrescription}
        />
      </Box>
    ),
    [clientId, patientId, editablePrescription],
  )

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      getHeader: renderHeader,
      getPrescription: getPrescriptionData,
      validate,
      getCurrentDoctorHasDvmLicense: () =>
        prescriptionBodyProps.currentDoctorHasDvmLicense,
    }),
    [
      renderHeader,
      getPrescriptionData,
      validate,
      prescriptionBodyProps.currentDoctorHasDvmLicense,
    ],
  )

  // Autoship feature will be implemented later for swap product flow
  const fieldsWithoutAutoship = {
    ...fields,
    autoshipFrequency: undefined,
    autoshipUnitId: undefined,
    enableAutoship: undefined,
  } as unknown as typeof fields

  const handleSignatureUpdate = useCallback(
    (signerId: string, signatureUrl: string) => {
      setSignatureOverride({
        signerId,
        signatureUrl,
        signer: undefined,
        signedDate: moment().toISOString(),
      })
    },
    [],
  )

  const signatureDataWithLocalState = useMemo(
    () => ({
      ...signatureData,
      ...(signatureOverride != null
        ? {
            signerId: signatureOverride.signerId,
            signatureUrl: signatureOverride.signatureUrl,
            signer: signatureOverride.signer,
            signedDate: signatureOverride.signedDate,
          }
        : {}),
    }),
    [signatureData, signatureOverride],
  )

  return (
    <Box className={classes.container}>
      <PrescriptionBody
        {...prescriptionBodyProps}
        activeWorkflow={activeWorkflow}
        fields={fieldsWithoutAutoship}
        handleSetActiveWorkflow={handleSetActiveWorkflow}
        isCompoundingReasonRequired={isCompoundingReasonRequired}
        isCustomCompound={isCustomCompound}
        prescription={currentPrescription}
        refs={refs}
        signatureData={signatureDataWithLocalState}
        validate={validate}
        onSignatureUpdate={handleSignatureUpdate}
      />
    </Box>
  )
})
